import React, { Fragment, useState, useEffect } from "react";
import {
	makeStyles,
	Hidden,
	Dialog,
	Typography,
	Box,
	Grid,
	Button,
	Slide,
	Drawer,
	useTheme,
	Link,
} from "@material-ui/core";
import { TransitionProps } from "@material-ui/core/transitions";
import { KeyboardArrowDown } from "@material-ui/icons";

import { getCookie, setCookie } from "src/utils/utils";
import useRouteManager from "src/utils/customHook/useRouteManager";

const TransitionUP = React.forwardRef(function Transition(
	props: TransitionProps & { children?: React.ReactElement<any, any> },
	ref: React.Ref<unknown>
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
	header: {
		color: theme.palette.primary.main,
	},
	description: {
		color: theme.palette.text.primary,
	},
	descriptionLink: {
		color: theme.palette.primary.main,
		textDecoration: "underline",
		"&:hover": {
			cursor: "pointer",
		},
	},
	greenButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.background.default,
		fontWeight: 500,
		fontSize: "13px",
		padding: "6px 10px",
		lineHeight: "20.77px",
		borderRadius: "8px",
	},
	transButton: {
		color: theme.palette.primary.main,
		textDecoration: "underline",
		fontWeight: 500,
		fontSize: "13px",
		padding: "5px",
		"&:hover": {
			backgroundColor: "transparent",
		},
	},
	zeroStateDialog: {
		position: "absolute",
		bottom: theme.spacing(1),
		padding: "20px 35px",
		borderRadius: "8px",
		pointerEvents: "auto",
		zIndex: theme.zIndex.drawer+110,
	},
	firstStateDialog: {
		padding: "20px 35px 60px 35px",
		borderRadius: "20px",
	},
	iconButton: {
		"&:hover": {
			cursor: "pointer",
		},
	},
	stateDrawer: {
		margin: "auto",
		borderRadius: "20px 20px 0 0",
		width: 450,
		[theme.breakpoints.down("xs")]: {
			width: "100%",
		},
		pointerEvents: "auto",
	},
	zeroStateDrawerContainer: {
		padding: "20px 50px",
		textAlign: "center",
	},
	firstStateDrawerContainer: {
		padding: "20px 50px 40px 50px",
		textAlign: "center",
	},
}));

const PDPACookie: React.FC = ({ children }) => {
	const classes = useStyles();
	const theme = useTheme();
	const { getRedirectPathname } = useRouteManager();
	const pdpaAllow = getCookie("CONSENT");
	const [hasConsent, setHasConsent] = useState<boolean>(Boolean(pdpaAllow));
	const [infoState, setInfoState] = useState<number>(-1); // 0: initial , 1: more information
	const handleApprove = () => {
		setCookie("CONSENT", true, 365);
		setHasConsent(true);
	};

	const handleInfoState = (state: number) => {
		if (state !== 0 && state !== 1) state = 0;
		setInfoState(state);
	};

	const handleCookiePolicy = () => {
		window.open("/cookiepolicy");
	};

	const renderDesktop = () => {
		return (
			<Fragment>
				{/* InfoState = 0 */}
				<Dialog
					open={infoState === 0 && !hasConsent}
					fullWidth
					maxWidth="md"
					TransitionComponent={TransitionUP}
					classes={{ paper: classes.zeroStateDialog }}
					BackdropProps={{
						style: {
							backgroundColor: "transparent",
						},
					}}
					hideBackdrop
					// disableBackdropClick
					disableScrollLock
					disableEnforceFocus
					disableAutoFocus
					style={{ pointerEvents: "none"}}>
					<Box>
						<Grid container spacing={2}>
							<Grid item xs={10}>
								<Typography className={classes.header} variant="body2">
									เว็บไซต์นี้ใช้คุกกี้
								</Typography>
								<Box mb={1} />
								<Typography className={classes.description} variant="caption">
									Finnize ใช้คุกกี้เพื่อเพิ่มประสิทธิภาพ
									และประสบการณ์ที่ดีในการใช้งานเว็บไซต์
									คุณสามารถเลือกตั้งค่าความยินยอมการใช้คุกกี้ได้ โดยคลิก
									"การตั้งค่าคุกกี้"{" "}
									<Link
										className={classes.descriptionLink}
										variant="caption"
										target="_blank"
										href={getRedirectPathname('/cookiepolicy')}
									>
										นโยบายความเป็นส่วนตัว
									</Link>
								</Typography>
							</Grid>
							<Grid
								item
								xs={2}
								style={{
									display: "flex",
									flexWrap: "wrap",
									alignItems: "center",
								}}>
								<Button
									onClick={handleApprove}
									fullWidth
									className={classes.greenButton}>
									ยอมรับทั้งหมด
								</Button>
								<Box style={{ flexBasis: "100%", height: "0" }} />
								<Button
									onClick={() => handleInfoState(1)}
									fullWidth
									className={classes.transButton}>
									รายละเอียดคุกกี้
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Dialog>

				{/* InfoState = 1 */}
				<Dialog
					open={infoState === 1 && !hasConsent}
					fullWidth
					maxWidth="sm"
					classes={{ paper: classes.firstStateDialog }}
					TransitionComponent={TransitionUP}
					disableScrollLock>
					<Box style={{ display: "flex", alignItems: "center" }} mb={3}>
						<Box
							style={{ display: "inline-block" }}
							className={classes.iconButton}
							mr={2}
							onClick={() => handleInfoState(0)}>
							<KeyboardArrowDown color="primary" />
						</Box>

						<Box style={{ display: "inline-block" }} mr={2}>
							<Typography className={classes.header} variant="body2">
								รายละเอียดคุกกี้
							</Typography>
						</Box>
						<Box style={{ display: "inline-block" }}>
							<Button
								onClick={handleApprove}
								className={classes.greenButton}
								style={{ display: "inline-flex" }}>
								ยอมรับทั้งหมด
							</Button>
						</Box>
					</Box>
					<Box>
						<Typography variant="body2" className={classes.header}>
							คุกกี้คืออะไร?
						</Typography>
						<Box mb={2} />
						<Typography variant="caption" className={classes.description}>
							คุกกี้ คือ ไฟล์ข้อมูลขนาดเล็กที่ถูกดาวน์โหลดไปยังเว็บเบราว์เซอร์
							อุปกรณ์คอมพิวเตอร์ อุปกรณ์เชื่อมต่ออินเตอร์เน็ต เช่น สมาร์ทโฟน
							หรือแท็บเล็ตของคุณ เพื่อทำหน้าที่บันทึกข้อมูลและการตั้งค่าต่างๆ
							เช่น สถานะการเข้าใช้งานในปัจจุบันของคุณ
							ข้อมูลการตั้งค่าภาษาข้อมูลเกี่ยวกับประวัติการเข้าใช้งานบนเว็บไซต์และ/หรือ
							แอปพลิเคชันที่คุณชื่นชอบเพื่อให้คุณสามารถใช้บริการที่มีการใช้งานคุกกี้
							หรือเทคโนโลยีอื่นใดที่มีลักษณะใกล้เคียงกันได้อย่างต่อเนื่อง ​
						</Typography>
						<Box mb={3} />

						<Typography variant="body2" className={classes.header}>
							คุกกี้จำเป็นไหม?
						</Typography>
						<Box mb={2} />
						<Typography variant="caption" className={classes.description}>
							คุกกี้ประเภทนี้มีความสำคัญในการทำให้คุณสามารถเยี่ยมชมและใช้บริการเว็บไซต์
							Finnize ได้ และในการให้บริการและฟังก์ชันต่างๆ บนเว็บไซต์ของ
							Finnize อาทิ การเข้าสู่พื้นที่ปลอดภัยบนเว็บไซต์ของระบบ
							หากไม่มีคุกกี้ประเภทนี้ Finnize
							หรือบางฟังก์ชันบนเว็บไซต์อาจจะไม่สามารถทำงานได้ตามปกติ
							รวมทั้งคุณจะไม่สามารถใช้บริการบางอย่างตามที่ร้องขอบนระบบของ
							Finnizeได้ ​
						</Typography>
					</Box>
				</Dialog>
			</Fragment>
		);
	};

	const renderMobile = () => {
		return (
			<Fragment>
				{/* InfoState = 0 */}
				<Drawer
					anchor={"bottom"}
					classes={{ paper: classes.stateDrawer }}
					open={infoState === 0 && !hasConsent}
					variant="temporary"
					BackdropProps={{ style: { backgroundColor: "transparent" } }}
					hideBackdrop
					// disableBackdropClick
					disableEnforceFocus
					disableScrollLock
					style={{ pointerEvents: "none", zIndex: theme.zIndex.drawer - 1 }}>
					<Box className={classes.zeroStateDrawerContainer}>
						<Typography className={classes.header} variant="body2">
							เว็บไซต์นี้ใช้คุกกี้
						</Typography>
						<Box mb={2} />
						<Typography className={classes.description} variant="caption">
							Finnize ใช้คุกกี้เพื่อเพิ่มประสิทธิภาพ
							และประสบการณ์ที่ดีในการใช้งานเว็บไซต์
							คุณสามารถเลือกตั้งค่าความยินยอมการใช้คุกกี้ได้ โดยคลิก
							"การตั้งค่าคุกกี้"{" "}
							<Link
								className={classes.descriptionLink}
								variant="caption"
								target="_blank"
								href={getRedirectPathname('/cookiepolicy')}
							>
								นโยบายความเป็นส่วนตัว
							</Link>
						</Typography>
						<Box mb={2} />
						<Box style={{ display: "flex", justifyContent: "center" }}>
							<Button
								onClick={() => handleInfoState(1)}
								className={classes.transButton}>
								รายละเอียดคุกกี้
							</Button>
							<Box mr={2} />
							<Button onClick={handleApprove} className={classes.greenButton}>
								ยอมรับทั้งหมด
							</Button>
						</Box>
					</Box>
				</Drawer>

				{/* InfoState = 1 */}
				<Drawer
					anchor={"bottom"}
					classes={{ paper: classes.stateDrawer }}
					open={infoState === 1 && !hasConsent}
					variant="temporary"
					disableScrollLock>
					<Box className={classes.firstStateDrawerContainer}>
						<Box style={{ display: "flex", alignItems: "center" }} mb={3}>
							<Box
								style={{ display: "inline-block" }}
								className={classes.iconButton}
								mr={2}
								onClick={() => handleInfoState(0)}>
								<KeyboardArrowDown color="primary" />
							</Box>

							<Box style={{ display: "inline-block" }} mr={2}>
								<Typography className={classes.header} variant="body2">
									รายละเอียดคุกกี้
								</Typography>
							</Box>
							<Box style={{ display: "inline-block" }}>
								<Button
									onClick={handleApprove}
									className={classes.greenButton}
									style={{ display: "inline-flex" }}>
									ยอมรับทั้งหมด
								</Button>
							</Box>
						</Box>
						<Box style={{ textAlign: "left" }}>
							<Typography variant="body2" className={classes.header}>
								คุกกี้คืออะไร?
							</Typography>
							<Box mb={2} />
							<Typography variant="caption" className={classes.description}>
								คุกกี้ คือ ไฟล์ข้อมูลขนาดเล็กที่ถูกดาวน์โหลดไปยังเว็บเบราว์เซอร์
								อุปกรณ์คอมพิวเตอร์ อุปกรณ์เชื่อมต่ออินเตอร์เน็ต เช่น สมาร์ทโฟน
								หรือแท็บเล็ตของคุณ เพื่อทำหน้าที่บันทึกข้อมูลและการตั้งค่าต่างๆ
								เช่น สถานะการเข้าใช้งานในปัจจุบันของคุณ
								ข้อมูลการตั้งค่าภาษาข้อมูลเกี่ยวกับประวัติการเข้าใช้งานบนเว็บไซต์และ/หรือ
								แอปพลิเคชันที่คุณชื่นชอบเพื่อให้คุณสามารถใช้บริการที่มีการใช้งานคุกกี้
								หรือเทคโนโลยีอื่นใดที่มีลักษณะใกล้เคียงกันได้อย่างต่อเนื่อง ​
							</Typography>
							<Box mb={3} />

							<Typography variant="body2" className={classes.header}>
								คุกกี้จำเป็นไหม?
							</Typography>
							<Box mb={2} />
							<Typography variant="caption" className={classes.description}>
								คุกกี้ประเภทนี้มีความสำคัญในการทำให้คุณสามารถเยี่ยมชมและใช้บริการเว็บไซต์
								Finnize ได้ และในการให้บริการและฟังก์ชันต่างๆ บนเว็บไซต์ของ
								Finnize อาทิ การเข้าสู่พื้นที่ปลอดภัยบนเว็บไซต์ของระบบ
								หากไม่มีคุกกี้ประเภทนี้ Finnize
								หรือบางฟังก์ชันบนเว็บไซต์อาจจะไม่สามารถทำงานได้ตามปกติ
								รวมทั้งคุณจะไม่สามารถใช้บริการบางอย่างตามที่ร้องขอบนระบบของ
								Finnizeได้ ​
							</Typography>
						</Box>
					</Box>
				</Drawer>
			</Fragment>
		);
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			handleInfoState(0);
		}, 3000);
		return () => clearTimeout(timer);
	}, []);

	return (
		<Fragment>
			{children}
			{/* Desktop */}
			<Hidden smDown>{renderDesktop()}</Hidden>

			{/* Mobile */}
			<Hidden mdUp>{renderMobile()}</Hidden>
		</Fragment>
	);
};

export default PDPACookie;
